/**
 * Pure geo maths for Tomt-modus. The rig lives in a local metric frame (x = east, z = south, metres). A `site`
 * object anchors that frame to the world: (anchor_x, anchor_z) is the rig-frame point that sits on (lat, lng), and
 * bearing_deg rotates the map under the rig (positive = map turned clockwise when seen from above).
 */

export const EARTH_CIRCUMFERENCE = 40075016.686; // metres at the equator
const M_PER_DEG_LAT = 111320;

export const toRad = (deg) => (deg * Math.PI) / 180;

/** lat/lng -> fractional web-mercator tile coordinates at zoom z. */
export function tileCoords(lat, lng, z) {
  const n = 2 ** z;
  const rad = toRad(lat);
  return { fx: ((lng + 180) / 360) * n, fy: ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n, n };
}

/** Ground metres covered by one web-mercator tile at this latitude/zoom. */
export const tileSizeM = (lat, z) => (EARTH_CIRCUMFERENCE * Math.cos(toRad(lat))) / 2 ** z;

/** Rig-frame point (x, z) -> lat/lng, given the site anchor + bearing. */
export function worldToLatLng(site, x, z) {
  if (!site || site.lat == null || site.lng == null) return null;
  const b = toRad(site.bearing_deg || 0);
  const dx = x - (site.anchor_x || 0);
  const dz = z - (site.anchor_z || 0);
  // the map group is rotated by +b about Y, so map-local = R(-b) · rig-frame
  const lx = dx * Math.cos(b) - dz * Math.sin(b);
  const lz = dx * Math.sin(b) + dz * Math.cos(b);
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(toRad(site.lat));
  return { lat: site.lat - lz / M_PER_DEG_LAT, lng: site.lng + lx / mPerDegLng };
}

/** lat/lng -> rig-frame point (x, z) for the current site anchor + bearing. */
export function latLngToWorld(site, lat, lng) {
  if (!site || site.lat == null || site.lng == null) return null;
  const b = toRad(site.bearing_deg || 0);
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(toRad(site.lat));
  const lx = (lng - site.lng) * mPerDegLng;
  const lz = -(lat - site.lat) * M_PER_DEG_LAT;
  // rig-frame = R(+b) · map-local
  const dx = lx * Math.cos(b) + lz * Math.sin(b);
  const dz = -lx * Math.sin(b) + lz * Math.cos(b);
  return { x: (site.anchor_x || 0) + dx, z: (site.anchor_z || 0) + dz };
}

/** Direction of true north in the rig frame (unit vector) for a given bearing. */
export function northVector(bearingDeg) {
  const b = toRad(bearingDeg || 0);
  return { x: -Math.sin(b), z: -Math.cos(b) };
}

const fmtDeg = (v, digits) => Math.abs(v).toLocaleString("nb-NO", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** "60,81234° N · 5,03421° Ø" – 5 decimals ≈ 1 m. */
export function fmtLatLng(lat, lng, digits = 5) {
  if (lat == null || lng == null) return "–";
  return `${fmtDeg(lat, digits)}° ${lat >= 0 ? "N" : "S"} · ${fmtDeg(lng, digits)}° ${lng >= 0 ? "Ø" : "V"}`;
}

/** Normalise any angle to 0–359.9. */
export const normBearing = (deg) => ((Number(deg) % 360) + 360) % 360;

/** Cardinal label for a bearing, e.g. 45 -> "NØ". */
export function bearingLabel(deg) {
  const names = ["N", "NØ", "Ø", "SØ", "S", "SV", "V", "NV"];
  return names[Math.round(normBearing(deg) / 45) % 8];
}

/** Zoom presets exposed to users – resolution vs. coverage. */
export const SITE_DETAIL = [
  { value: 17, label: "Oversikt", hint: "≈ 1 km rundt" },
  { value: 18, label: "Standard", hint: "≈ 500 m rundt" },
  { value: 19, label: "Nær", hint: "≈ 250 m rundt" },
];
